import React from "react";
import { Howl, Howler } from "howler";
import getPublicAssetUrl from "utils/PublicAsset";

type SoundName = "hover" | "click" | "putCard" | "flipCard" | "uno" | "block" | "notification";

type SoundContextValue = {
    play: (name: SoundName) => void;
};

const ANTI_FLOOD_SOUNDS: SoundName[] = ["flipCard"];

const SoundContext = React.createContext<SoundContextValue>({ play: () => undefined });

export const useSounds = () => React.useContext(SoundContext);

type SoundProviderProps = {
    children: React.ReactNode;
    muted?: boolean;
    volume?: number;
};

export const SoundProvider = ({ children, muted = false, volume = 0.2 }: SoundProviderProps) => {
    const sounds = React.useRef<Record<string, { howl: Howl; loaded: boolean }>>({});

    React.useEffect(() => {
        const unlockAudio = () => {
            const ctx = (Howler as unknown as { ctx?: AudioContext }).ctx;

            if(ctx && ctx.state !== "running") {
                ctx.resume?.();
            }

            document.removeEventListener("touchstart", unlockAudio);
            document.removeEventListener("click", unlockAudio);
        };

        document.addEventListener("touchstart", unlockAudio, { once: true });
        document.addEventListener("click", unlockAudio, { once: true });

        const onVisibilityChange = () => {
            if(document.visibilityState === "visible") {
                const ctx = (Howler as unknown as { ctx?: AudioContext }).ctx;

                if(ctx && ctx.state !== "running") {
                    ctx.resume?.();
                }
            }
        };

        document.addEventListener("visibilitychange", onVisibilityChange);

        const entries: Array<[string, string]> = [
            ["hover", getPublicAssetUrl("assets/sounds/hover.mp3")],
            ["click", getPublicAssetUrl("assets/sounds/focus.mp3")],
            ["putCard", getPublicAssetUrl("assets/sounds/put_card.mp3")],
            ["flipCard", getPublicAssetUrl("assets/sounds/flip_card.mp3")],
            ["uno", getPublicAssetUrl("assets/sounds/uno.mp3")],
            ["block", getPublicAssetUrl("assets/sounds/block.mp3")],
            ["notification", getPublicAssetUrl("assets/sounds/notification.mp3")],
        ];

        for(const [name, src] of entries) {
            const howl = new Howl({ src: [src], preload: true, pool: 4 });
            const entry = { howl, loaded: false };

            howl.once("load", () => { entry.loaded = true; });
            howl.once("loaderror", () => { entry.loaded = false; });

            sounds.current[name] = entry;
        }

        return () => {
            document.removeEventListener("visibilitychange", onVisibilityChange);

            for(const entry of Object.values(sounds.current)) {
                entry.howl.unload();
            }

            sounds.current = {};
        };
    }, []);

    React.useEffect(() => {
        Howler.volume(volume ?? 0);
    }, [volume]);

    React.useEffect(() => {
        Howler.mute(muted);
    }, [muted]);

    const play = React.useCallback((name: SoundName) => {
        const entry = sounds.current[name];

        if(!entry) {
            return;
        }

        const ctx = (Howler as unknown as { ctx?: AudioContext }).ctx;

        if(!ctx || ctx.state !== "running") {
            return;
        }

        if(!entry.loaded) {
            return;
        }

        if(ANTI_FLOOD_SOUNDS.includes(name) && entry.howl.playing()) {
            return;
        }

        try {
            entry.howl.play();
        } catch(_) {
            // ignore
        }
    }, []);

    const value = React.useMemo(() => ({ play }), [play]);

    return (
        <SoundContext.Provider value={value}>
            {children}
        </SoundContext.Provider>
    );
};
